import { Loader, Stack, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

export function PdfViewer({ url }: { url: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // pdfjs-dist v6 requires an object param — plain string is rejected
        const pdf = await pdfjs.getDocument({ url }).promise;
        if (cancelled || !containerRef.current) return;
        containerRef.current.innerHTML = "";

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          if (cancelled) break;
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.5 });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.cssText =
            "display:block;margin:0 auto 8px;max-width:100%;box-shadow:0 2px 8px rgba(0,0,0,0.4)";

          const ctx = canvas.getContext("2d");
          // pdfjs-dist v6 requires the canvas element itself in the render
          // params, alongside the 2D context and viewport.
          if (ctx) await page.render({ canvas, canvasContext: ctx, viewport }).promise;

          if (!cancelled && containerRef.current) {
            containerRef.current.appendChild(canvas);
          }
        }
      } catch (e) {
        console.error("[PdfViewer] load error:", e);
        const msg = e instanceof Error ? e.message : String(e);
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div style={{ height: "100%", overflow: "auto", background: "#525659", padding: "16px" }}>
      {loading && !error && (
        <Stack align="center" justify="center" style={{ height: "100%" }}>
          <Loader size="sm" color="white" />
          <Text size="xs" c="white">טוען מסמך…</Text>
        </Stack>
      )}
      {error && (
        <Stack align="center" justify="center" style={{ height: "100%", padding: "24px" }}>
          <Text c="red.3" size="sm" ta="center" style={{ wordBreak: "break-word" }}>
            {error}
          </Text>
        </Stack>
      )}
      <div ref={containerRef} />
    </div>
  );
}
